import { useRequiredContext } from "@redotech/react-util/context";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Flex } from "../flex";
import { Text } from "../text";
import * as styles from "./setting-section.module.css";
import { TabNavigationContext } from "./tab-navigation-provider";

export const SettingSection = memo(function SettingSection({
  children,
  title,
  description,
  id,
  collapsible = false,
  defaultCollapsed = false,
  errors = [],
  titleBadge,
}: {
  children?: React.ReactNode;
  title: string;
  description?: string;
  id: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  errors?: string[];
  /** Status chip rendered inline beside the title. */
  titleBadge?: React.ReactNode;
}) {
  const { addSectionRef, removeSectionRef, setSectionErrors } =
    useRequiredContext(TabNavigationContext);

  useEffect(() => {
    setSectionErrors(id, errors);
  }, [id, errors, setSectionErrors]);

  const elementRef = useRef<HTMLDivElement | null>(null);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => !prev);
  }, []);

  useEffect(() => {
    addSectionRef(id, title, elementRef.current);

    return () => {
      removeSectionRef(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, title]);

  return (
    <div className={styles.container} id={id} ref={elementRef}>
      <div
        className={collapsible ? styles.collapsibleHeader : undefined}
        onClick={collapsible ? toggleCollapsed : undefined}
      >
        <Flex align="center" justify="space-between">
          <Flex dir="column">
            <Flex align="center" gap="md">
              <Text fontSize="xl" fontWeight="semibold" textColor="primary">
                {title}
              </Text>
              {titleBadge}
            </Flex>
            {description ? (
              <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
                {description}
              </Text>
            ) : undefined}
          </Flex>
          {collapsible && (
            <ChevronDownIcon
              className={`${styles.chevron} ${collapsed ? styles.chevronCollapsed : ""}`}
            />
          )}
        </Flex>
      </div>
      {!collapsed && children}
    </div>
  );
});
